import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { getRouteConfig } from "@/data/navigation";
import styles from "./PageTransition.module.css";

const EXPO_EASE = [0.16, 1, 0.3, 1] as [number, number, number, number];

function getDirectionalVariants(from: "left" | "right" | "bottom") {
  const offscreen = {
    left: { x: "-100%", y: 0 },
    right: { x: "100%", y: 0 },
    bottom: { x: 0, y: "100%" },
  }[from];

  return {
    initial: { opacity: 0, x: offscreen.x, y: offscreen.y },
    animate: { opacity: 1, x: 0, y: 0 },
    exit: { opacity: 0, x: offscreen.x, y: offscreen.y },
  };
}

const FIRST_LOAD_CLASS = {
  left: styles.pageEnterLeft,
  right: styles.pageEnterRight,
  bottom: styles.pageEnterBottom,
};

/**
 * Has a page already mounted in this browser tab? The first one is the
 * server-rendered page: TransitionProvider renders it at rest
 * (AnimatePresence initial={false}) and it plays its entrance in CSS from
 * first paint. Waiting for hydration to start a framer-motion entrance kept
 * every page invisible for ~5s on a throttled phone (LCP). On the server this
 * never flips, so every prerendered page gets the CSS entrance.
 */
let firstPageMounted = false;

function useIsFirstLoad(): boolean {
  const [isFirstLoad] = useState(() => !firstPageMounted);
  useEffect(() => {
    firstPageMounted = true;
  }, []);
  return isFirstLoad;
}

/**
 * Land on the address's #hash after a client navigation (e.g. "← ALL
 * COLLECTIONS" → /shop#collections). Vike scrolls to the hash as soon as it
 * renders, but AnimatePresence mode="wait" mounts the incoming page only after
 * the outgoing one has exited, so the target isn't there yet and Vike does
 * nothing. This runs once the incoming page's DOM exists. The first page load
 * is left to the browser, which already scrolls to the hash.
 */
function useLandOnHash(isFirstLoad: boolean) {
  useEffect(() => {
    if (isFirstLoad) return;
    const id = decodeURIComponent(window.location.hash.slice(1));
    if (id) document.getElementById(id)?.scrollIntoView();
  }, [isFirstLoad]);
}

export default function PageTransition({
  children,
  pathname,
}: {
  children: React.ReactNode;
  pathname: string;
}) {
  const route = getRouteConfig(pathname);
  const from = route?.transitionFrom ?? "right";
  const isFirstLoad = useIsFirstLoad();
  const firstLoadClass = isFirstLoad
    ? pathname === "/" ? styles.pageEnterFade : FIRST_LOAD_CLASS[from]
    : undefined;
  useLandOnHash(isFirstLoad);

  /**
   * `willChange: "transform"` keeps this wrapper as a containing block for
   * `position: fixed` descendants (e.g. SectionDeck) even after the slide-in
   * animation ends and framer-motion may collapse `transform: translate(0,0)`
   * back to `transform: none`. Without it, the deck's "fixed" anchor shifts
   * from wrapper to viewport when the animation completes, and centered
   * content visibly jumps to a new position.
   *
   * Because the deck is sized against this wrapper (top: nav, bottom: 0), the
   * wrapper must be at least a viewport tall — otherwise the deck collapses
   * to zero height and takes no gestures.
   */
  const containingBlockStyle: React.CSSProperties = {
    minHeight: "100vh",
    willChange: "transform",
  };

  if (pathname === "/") {
    return (
      <motion.div
        key={pathname}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.5, ease: EXPO_EASE }}
        style={containingBlockStyle}
        className={firstLoadClass}
      >
        {children}
      </motion.div>
    );
  }

  const variants = getDirectionalVariants(from);

  return (
    <motion.div
      key={pathname}
      initial={variants.initial}
      animate={variants.animate}
      exit={variants.exit}
      transition={{ duration: 0.65, ease: EXPO_EASE }}
      style={containingBlockStyle}
      className={firstLoadClass}
    >
      {children}
    </motion.div>
  );
}
